import type {PricedHotelMetaDistances} from '@/api/bffHotel';

export type {OfferAvailablePayments as PaymentMethod} from '@/api/bffOffer';

export {OfferAvailablePayments as PaymentMethods} from '@/api/bffOffer';

// request parameter (different to the one above for some reason)
export const BFF_RATING_TYPE_AMADEUS = 'ratingAmadeus' as const;

export type Price = {
  amount: number;
  currency: string;
};

export type DiscountType =
  | 'NO_DISCOUNT'
  | 'SPECIAL_DISCOUNT'
  | 'ISEARCH_DISCOUNT'
  | 'PRICE_CACHE';

export type InviaType = {
  type: string;
  name: string;
};

export type Room = {
  type: InviaType;
  view?: InviaType;
  boardType: InviaType;
  attributes: null | Array<InviaType>;
  handicapped_accessible: boolean;
  roomCode: string;
  supplierType: string;
  adults: number;
  children: null | Array<number>;
};

export type Sponsored = {
  impressionUrl: string;
  viewableImpressionUrl: string;
  conversionUrl: string;
  clickUrl: string;
  flightId: number;
  creativeContent?: {
    companionText: string;
    tourOperator: string;
  };
};

// Inventory
// ---------

export const INVENTORY_CATEGORY_ROOM_TYPE = 'ROOMCODE' as const;
export const INVENTORY_CATEGORY_ROOM_VIEW = 'ROOMVIEW' as const;
export const INVENTORY_CATEGORY_ROOM_ATTRIBUTES = 'ROOMATTRIBUTES' as const;
export const INVENTORY_CATEGORY_BOARD_TYPE = 'BOARDCODE' as const;
export const INVENTORY_CATEGORY_TOUR_OPERATOR = 'TOUROPERATOR' as const;
export const INVENTORY_CATEGORY_DURATION = 'DURATION' as const;
export const INVENTORY_CATEGORY_TRANSFER = 'TRANSFER' as const;
export const INVENTORY_CATEGORY_AIRLINE = 'AIRLINE' as const;
export const INVENTORY_CATEGORY_DESTINATION_AIRPORT =
  'DESTINATION_AIRPORT' as const;

export type InventoryCategoryType =
  | typeof INVENTORY_CATEGORY_ROOM_TYPE
  | typeof INVENTORY_CATEGORY_ROOM_VIEW
  | typeof INVENTORY_CATEGORY_ROOM_ATTRIBUTES
  | typeof INVENTORY_CATEGORY_BOARD_TYPE
  | typeof INVENTORY_CATEGORY_TOUR_OPERATOR
  | typeof INVENTORY_CATEGORY_DURATION
  | typeof INVENTORY_CATEGORY_TRANSFER
  | typeof INVENTORY_CATEGORY_AIRLINE
  | typeof INVENTORY_CATEGORY_DESTINATION_AIRPORT;

export type InventoryCategoryVariation = {
  value: string;
  /**
   * @deprecated use "priceDisplay" instead
   */
  price: number;
  priceDisplay?: Price;
  translation?: string;
  count: number;
  lng?: number;
  lat?: number;
};

export type InventoryCategoryVariations = Array<InventoryCategoryVariation>;

export type InventoryCategory = {
  name: InventoryCategoryType;
  translation: string;
  variations: InventoryCategoryVariations;
};

export type Inventory = Array<InventoryCategory>;

// Meta
// ----

export const PROMOTION_CATEGORY_SPECIAL = 'specials' as const;
export const PROMOTION_CATEGORY_FLEX = 'flex' as const;
export const PROMOTION_CATEGORY_SAFETY = 'safety' as const;

export type PromotionCategory =
  | typeof PROMOTION_CATEGORY_SPECIAL
  | typeof PROMOTION_CATEGORY_FLEX
  | typeof PROMOTION_CATEGORY_SAFETY;

type MetaTourOperatorSpecialItem = {
  type: string;
  translation: string;
  tooltip?: string;
  count: number;
  /**
   * @deprecated Use priceDisplay instead
   */
  price: number;
  priceDisplay: Price;
  tourOperators: Array<string>;
};

export type MetaTourOperatorSpecial = {
  type: string;
  translation: string;
  tooltip: string;
  variations?: null | Array<MetaTourOperatorSpecialItem>;
};

// keys must match PROMOTION_CATEGORY_* constants
type MetaTourOperatorSpecials = {
  flex?: MetaTourOperatorSpecial;
  specials?: MetaTourOperatorSpecial;
  safety?: MetaTourOperatorSpecial;
};

type MetaAttributeGroupItem = {
  count: number;
  /**
   * @deprecated Use priceDisplay instead
   */
  price: number;
  priceDisplay: Price;
  translation: string;
};

type MetaAttributeGroupItems = Record<string, MetaAttributeGroupItem>;

export type MetaAttributeGroup = {
  translation: string;
  items: MetaAttributeGroupItems;
};

type MetaAttributes = Record<string, MetaAttributeGroup>;

type MetaCategory = {
  count?: number;
  /**
   * @deprecated Use priceDisplay instead
   */
  price?: number;
  priceDisplay: Price;
};
// @TODO consts?
export type MetaCategories = {
  category1: MetaCategory;
  category2: MetaCategory;
  category3: MetaCategory;
  category4: MetaCategory;
  category5: MetaCategory;
};

type MetaRating = {
  count?: number;
  /**
   * @deprecated Use priceDisplay instead
   */
  price?: number;
  priceDisplay: Price;
};
// @TODO consts
export type MetaRatings = {
  poor: MetaRating;
  fair: MetaRating;
  good: MetaRating;
  very_good: MetaRating;
  excellent: MetaRating;
};

type MetaPriceRange = {
  /**
   * @deprecated Use minDisplay instead
   */
  min?: number;
  minDisplay: Price;
  /**
   * @deprecated Use maxDisplay instead
   */
  max?: number;
  maxDisplay: Price;
};

export type MetaAirline = {
  code: string;
  name: string;
  price?: Price;
};

export type MetaAirlines = Array<MetaAirline>;

export type MetaArrivalAirport = {
  code: string;
  name: string;
  price?: Price;
  lat?: number;
  lon?: number;
  distance: number | null;
};

export type MetaArrivalAirports = Array<MetaArrivalAirport>;

export type MetaRoomAttribute = {
  type: string;
  name: string;
  price: Price;
};

export type MetaRoomAttributes = Array<MetaRoomAttribute>;

export type Region = {
  count: number;
  name: string;
  id: string;
  children?: Array<Region>;
};

export type Meta = {
  regions?: Array<Region>;
  attributes?: MetaAttributes;
  categories?: MetaCategories;
  ratings?: MetaRatings;
  priceRange?: MetaPriceRange;
  totalCount?: number;
  tourOperatorSpecials?: MetaTourOperatorSpecials;
  airlines?: MetaAirlines;
  roomAttributes?: MetaRoomAttributes;
  arrivalAirports?: MetaArrivalAirports;
  distances?: PricedHotelMetaDistances;
};

export type CrossBordersData = {
  airports: Array<string>;
  priceTotal: Price;
  url: string;
};
